/**
 * Hook personalizado para integração com Mercado Pago
 * 
 * Este hook facilita a inicialização e uso do SDK do Mercado Pago
 * em componentes React.
 */

import { useState, useEffect } from 'react';

declare global {
  interface Window {
    MercadoPago: any;
  }
}

/**
 * Opções de configuração do hook
 */
interface UseMercadoPagoOptions {
  publicKey: string;
  locale?: string;
}

/**
 * Hook para inicializar e gerenciar o SDK do Mercado Pago
 * 
 * @param options Opções de configuração
 * @returns Objeto com instância do Mercado Pago e estado de carregamento
 */
const useMercadoPago = (options: UseMercadoPagoOptions) => {
  const [mercadoPagoInstance, setMercadoPagoInstance] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Função para carregar o script do Mercado Pago
    const loadMercadoPagoScript = () => {
      return new Promise<void>((resolve, reject) => {
        // Verifica se o script já foi carregado
        if (window.MercadoPago) {
          resolve();
          return;
        }

        // Cria o elemento script
        const script = document.createElement('script');
        script.src = 'https://sdk.mercadopago.com/js/v2';
        script.type = 'text/javascript';
        script.async = true;
        
        // Define callbacks para sucesso e erro
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Falha ao carregar o SDK do Mercado Pago'));
        
        // Adiciona o script ao documento
        document.body.appendChild(script);
      });
    };

    // Função para inicializar o SDK
    const initializeMercadoPago = async () => {
      try {
        setLoading(true);
        
        // Carrega o script
        await loadMercadoPagoScript();
        
        // Inicializa o SDK com a chave pública
        const mp = new window.MercadoPago(options.publicKey, {
          locale: options.locale || 'pt-BR'
        });
        
        setMercadoPagoInstance(mp);
        setError(null);
      } catch (err) {
        console.error('Erro ao inicializar Mercado Pago:', err);
        setError(err instanceof Error ? err : new Error('Erro desconhecido'));
      } finally {
        setLoading(false);
      }
    };

    // Inicializa o SDK quando o componente for montado
    initializeMercadoPago();

    // Limpa o estado quando o componente for desmontado
    return () => {
      setMercadoPagoInstance(null);
    };
  }, [options.publicKey, options.locale]);

  return { mercadoPago: mercadoPagoInstance, loading, error };
};

export default useMercadoPago;
